import { FormattedMessage, defineMessage } from 'react-intl';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { PropTypes as PropTypes$1 } from '@micromag/core';
import { ScreenElement } from '@micromag/core/components';
import { useScreenSize, useScreenRenderContext, useViewerContext, useViewerWebView, usePlaybackContext, usePlaybackMediaRef } from '@micromag/core/contexts';
import { useTrackScreenMedia } from '@micromag/core/hooks';
import { isHeaderFilled, isFooterFilled, getFooterProps, isIos } from '@micromag/core/utils';
import Audio from '@micromag/element-audio';
import Background from '@micromag/element-background';
import ClosedCaptions from '@micromag/element-closed-captions';
import Container from '@micromag/element-container';
import Footer from '@micromag/element-footer';
import Header from '@micromag/element-header';
import Layout, { Spacer } from '@micromag/element-layout';
import _toConsumableArray from '@babel/runtime/helpers/toConsumableArray';
import { Audio as Audio$1, Container as Container$1 } from '@micromag/transforms/apple-news';

var styles = {"container":"micromag-screen-audio-container","background":"micromag-screen-audio-background","disabled":"micromag-screen-audio-disabled","hidden":"micromag-screen-audio-hidden","placeholder":"micromag-screen-audio-placeholder","content":"micromag-screen-audio-content","empty":"micromag-screen-audio-empty","audio":"micromag-screen-audio-audio","bottom":"micromag-screen-audio-bottom","isPreview":"micromag-screen-audio-isPreview"};

var propTypes = {
  layout: PropTypes.oneOf(['middle']),
  audio: PropTypes$1.audioElement,
  spacing: PropTypes.number,
  background: PropTypes$1.backgroundElement,
  header: PropTypes$1.header,
  footer: PropTypes$1.footer,
  current: PropTypes.bool,
  active: PropTypes.bool,
  mediaRef: PropTypes.func,
  showWave: PropTypes.bool,
  className: PropTypes.string
};
var defaultProps = {
  layout: 'middle',
  audio: null,
  spacing: 20,
  background: null,
  header: null,
  footer: null,
  current: true,
  active: true,
  mediaRef: null,
  showWave: true,
  className: null
};
var AudioScreen = function AudioScreen(_ref) {
  _ref.layout;
    var audio = _ref.audio,
    spacing = _ref.spacing,
    background = _ref.background,
    header = _ref.header,
    footer = _ref.footer,
    current = _ref.current,
    active = _ref.active,
    customMediaRef = _ref.mediaRef,
    showWave = _ref.showWave,
    className = _ref.className;
  var _useScreenSize = useScreenSize(),
    width = _useScreenSize.width,
    height = _useScreenSize.height,
    resolution = _useScreenSize.resolution;
  var _useScreenRenderConte = useScreenRenderContext(),
    isPlaceholder = _useScreenRenderConte.isPlaceholder,
    isPreview = _useScreenRenderConte.isPreview,
    isView = _useScreenRenderConte.isView,
    isEdit = _useScreenRenderConte.isEdit,
    isStatic = _useScreenRenderConte.isStatic,
    isCapture = _useScreenRenderConte.isCapture;
  var _useViewerContext = useViewerContext(),
    viewerTopHeight = _useViewerContext.topHeight,
    viewerBottomHeight = _useViewerContext.bottomHeight,
    viewerBottomSidesWidth = _useViewerContext.bottomSidesWidth;
  var _useViewerWebView = useViewerWebView(),
    openWebView = _useViewerWebView.open;
  var trackScreenMedia = useTrackScreenMedia('audio');
  var _useState = useState(isStatic || isPlaceholder),
    _useState2 = _slicedToArray(_useState, 2),
    ready = _useState2[0],
    setReady = _useState2[1];
  var backgroundPlaying = current && (isView || isEdit);
  var mediaShouldLoad = current || active;
  // const transitionPlaying = current && ready;
  // const transitionDisabled = isStatic || isCapture || isPlaceholder || isPreview || isEdit;

  var hasHeader = isHeaderFilled(header);
  var hasFooter = isFooterFilled(footer);
  var footerProps = getFooterProps(footer, {
    isView: isView,
    current: current,
    openWebView: openWebView,
    isPreview: isPreview
  });
  var hasAudio = audio !== null;
  var _ref2 = audio || {},
    _ref2$media = _ref2.media,
    audioMedia = _ref2$media === void 0 ? null : _ref2$media,
    _ref2$autoPlay = _ref2.autoPlay,
    autoPlay = _ref2$autoPlay === void 0 ? true : _ref2$autoPlay,
    _ref2$closedCaptions = _ref2.closedCaptions,
    closedCaptions = _ref2$closedCaptions === void 0 ? null : _ref2$closedCaptions,
    _ref2$withWave = _ref2.withWave,
    withWave = _ref2$withWave === void 0 ? false : _ref2$withWave,
    _ref2$withControls = _ref2.withControls,
    withControls = _ref2$withControls === void 0 ? false : _ref2$withControls,
    _ref2$withSeekBar = _ref2.withSeekBar,
    withSeekBar = _ref2$withSeekBar === void 0 ? false : _ref2$withSeekBar,
    _ref2$color = _ref2.color,
    color = _ref2$color === void 0 ? null : _ref2$color,
    _ref2$progressColor = _ref2.progressColor,
    progressColor = _ref2$progressColor === void 0 ? null : _ref2$progressColor;
  var _ref3 = audioMedia || {},
    _ref3$url = _ref3.url,
    audioUrl = _ref3$url === void 0 ? null : _ref3$url;
  var hasAudioUrl = audioUrl !== null;
  var finalAudio = hasAudio ? _objectSpread(_objectSpread({}, audio), {}, {
    autoPlay: !isPreview && !isStatic && !isCapture && autoPlay && current
  }) : null;
  var hasClosedCaptions = closedCaptions !== null;
  var _usePlaybackContext = usePlaybackContext(),
    playing = _usePlaybackContext.playing,
    muted = _usePlaybackContext.muted,
    setControls = _usePlaybackContext.setControls,
    setControlsTheme = _usePlaybackContext.setControlsTheme,
    setPlaying = _usePlaybackContext.setPlaying;
  var mediaRef = usePlaybackMediaRef(current);
  useEffect(function () {
    if (!current) {
      return function () {};
    }
    if (withControls || withSeekBar) {
      setControls(true);
      setControlsTheme({
        seekBarOnly: withSeekBar,
        color: color,
        progressColor: progressColor
      });
    } else {
      setControls(false);
    }
    return function () {
      if (withControls || withSeekBar) {
        setControls(false);
      }
    };
  }, [current, withControls, withSeekBar, setControls, color, progressColor]);
  useEffect(function () {
    if (customMediaRef !== null) {
      customMediaRef(mediaRef.current);
    }
  }, [mediaRef.current]);
  var onAudioReady = useCallback(function () {
    setReady(true);
  }, [setReady]);
  var _useState3 = useState(null),
    _useState4 = _slicedToArray(_useState3, 2),
    currentTime = _useState4[0],
    setCurrentTime = _useState4[1];
  var _useState5 = useState(null),
    _useState6 = _slicedToArray(_useState5, 2),
    duration = _useState6[0],
    setDuration = _useState6[1];
  var isIOS = useMemo(function () {
    return isIos();
  }, [isIos]);
  useEffect(function () {
    if (current && autoPlay && !playing) {
      setPlaying(true);
    }
  }, [current, autoPlay]);
  var onTimeUpdate = useCallback(function (_ref4) {
    var _ref4$timeStamp = _ref4.timeStamp,
      timeStamp = _ref4$timeStamp === void 0 ? 0 : _ref4$timeStamp;
    setCurrentTime(timeStamp);
  }, [setCurrentTime, setDuration, duration]);
  var onProgressStep = useCallback(function (step) {
    trackScreenMedia(audio, "progress_".concat(Math.round(step * 100, 10), "%"));
  }, [trackScreenMedia, audio]);
  var onDurationChange = useCallback(function (dur) {
    setDuration(dur);
  }, [setDuration]);
  var onPlay = useCallback(function (_ref5) {
    var initial = _ref5.initial;
    trackScreenMedia(audio, initial ? 'play' : 'resume');
  }, [trackScreenMedia, audio]);
  var onPause = useCallback(function (_ref6) {
    var midway = _ref6.midway;
    trackScreenMedia(audio, midway ? 'pause' : 'ended');
  }, [trackScreenMedia, audio]);
  var onEnded = useCallback(function () {
    if (current) {
      setPlaying(false);
    }
  }, [current, setPlaying]);
  var onSeeked = useCallback(function (time) {
    if (time > 0) {
      trackScreenMedia(audio, 'seek');
    }
  }, [trackScreenMedia, audio]);
  return /*#__PURE__*/React.createElement("div", {
    className: classNames([styles.container, _defineProperty(_defineProperty(_defineProperty({}, className, className !== null), styles.placeholder, isPlaceholder), styles.isPreview, isPreview)]),
    "data-screen-ready": ready
  }, /*#__PURE__*/React.createElement(Container, {
    width: width,
    height: height,
    className: styles.content
  }, /*#__PURE__*/React.createElement(Layout, {
    fullscreen: true,
    style: !isPlaceholder ? {
      padding: spacing,
      paddingTop: (!isPreview ? viewerTopHeight : 0) + (hasHeader ? spacing / 2 : spacing)
    } : null
  }, hasHeader ? /*#__PURE__*/React.createElement(Header, header) : /*#__PURE__*/React.createElement(Spacer, {
    key: "spacer-top"
  }), /*#__PURE__*/React.createElement(ScreenElement, {
    key: "audio",
    placeholder: "audio",
    emptyLabel: /*#__PURE__*/React.createElement(FormattedMessage, {
      id: "MYZwSA",
      defaultMessage: [{
        "type": 0,
        "value": "Audio"
      }]
    }),
    emptyClassName: styles.empty,
    isEmpty: !hasAudioUrl
  }, /*#__PURE__*/React.createElement(Audio, Object.assign({}, finalAudio, {
    mediaRef: mediaRef,
    waveFake: isIOS || isPreview,
    waveProps: isPreview ? {
      sampleWidth: 10,
      sampleMargin: 5,
      minSampleHeight: 5,
      backgroundColor: color,
      progressColor: progressColor
    } : {
      backgroundColor: color,
      progressColor: progressColor
    },
    paused: !current || !playing,
    muted: muted,
    className: styles.audio,
    onReady: onAudioReady,
    onPlay: onPlay,
    onPause: onPause,
    onTimeUpdate: onTimeUpdate,
    onProgressStep: onProgressStep,
    onDurationChange: onDurationChange,
    onSeeked: onSeeked,
    onEnded: onEnded,
    withWave: showWave && withWave
  }))), /*#__PURE__*/React.createElement(Spacer, {
    key: "spacer-middle"
  }), !isPlaceholder ? /*#__PURE__*/React.createElement("div", {
    key: "bottom",
    className: styles.bottom,
    style: {
      transform: current && !isPreview ? "translate(0, -".concat(viewerBottomHeight, "px)") : null,
      paddingLeft: Math.max(spacing / 2, viewerBottomSidesWidth),
      paddingRight: Math.max(spacing / 2, viewerBottomSidesWidth),
      paddingBottom: spacing / 2,
      paddingTop: 0
    }
  }, hasClosedCaptions && !isPreview && !isCapture && !isStatic ? /*#__PURE__*/React.createElement(ClosedCaptions, {
    className: styles.closedCaptions,
    media: closedCaptions,
    currentTime: currentTime
  }) : null, hasFooter ? /*#__PURE__*/React.createElement(Footer, Object.assign({}, footerProps, {
    className: styles.callToAction
  })) : null) : null)), !isPlaceholder ? /*#__PURE__*/React.createElement(Background, {
    background: background,
    width: width,
    height: height,
    resolution: resolution,
    playing: backgroundPlaying,
    muted: muted,
    shouldLoad: mediaShouldLoad,
    withoutVideo: isPreview,
    className: styles.background
  }) : null);
};
AudioScreen.propTypes = propTypes;
AudioScreen.defaultProps = defaultProps;

var transform = function transform(newStory, _ref) {
  var audio = _ref.audio;
  var _ref2 = audio || {},
    _ref2$media = _ref2.media,
    media = _ref2$media === void 0 ? null : _ref2$media;
  var _Audio = Audio$1(newStory, media),
    titleStory = _Audio.story,
    titleComponent = _Audio.component;
  // console.log(media);
  var _Container = Container$1(titleStory, _toConsumableArray(titleComponent ? [titleComponent] : [])),
    containerStory = _Container.story,
    containerComponent = _Container.component;
  return _objectSpread(_objectSpread({}, containerStory), {}, {
    components: [].concat(_toConsumableArray(newStory.components || []), _toConsumableArray(containerComponent ? [containerComponent] : []))
  });
};

// eslint-disable-next-line

var transforms = /*#__PURE__*/Object.freeze({
    __proto__: null,
    appleNews: transform
});

var definition = {
  id: 'audio',
  type: 'screen',
  group: {
    label: defineMessage({
      id: "+9akmg",
      defaultMessage: [{
        "type": 0,
        "value": "Audio and Video"
      }]
    }),
    order: 5
  },
  title: defineMessage({
    id: "bhEaUt",
    defaultMessage: [{
      "type": 0,
      "value": "Audio"
    }]
  }),
  component: AudioScreen,
  layouts: ['middle'],
  transforms: transforms,
  fields: [{
    name: 'audio',
    type: 'audio-element',
    theme: {
      color: 'primary'
    },
    defaultValue: {
      autoPlay: true
    },
    label: defineMessage({
      id: "169xnt",
      defaultMessage: [{
        "type": 0,
        "value": "Audio"
      }]
    })
  }, {
    name: 'background',
    type: 'background',
    label: defineMessage({
      id: "+MPZRu",
      defaultMessage: [{
        "type": 0,
        "value": "Background"
      }]
    })
  }, {
    name: 'header',
    type: 'header',
    label: defineMessage({
      id: "rhuDxI",
      defaultMessage: [{
        "type": 0,
        "value": "Header"
      }]
    }),
    theme: {
      badge: {
        label: {
          textStyle: 'badge'
        },
        boxStyle: 'badge'
      }
    }
  }, {
    name: 'footer',
    type: 'footer',
    label: defineMessage({
      id: "g4nybp",
      defaultMessage: [{
        "type": 0,
        "value": "Footer"
      }]
    }),
    theme: {
      callToAction: {
        label: {
          textStyle: 'cta'
        },
        boxStyle: 'cta'
      }
    }
  }]
};

export { AudioScreen, definition as default };
